import { useState, useCallback, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { CustomerDetails } from '../types';

export function useCustomerDetailsManagement() {
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // Load customer details from localStorage on mount and when returning from customer details page
  useEffect(() => {
    const stored = localStorage.getItem('customerDetails');
    if (stored) {
      try {
        const parsedDetails = JSON.parse(stored);
        // Convert date strings back to Date objects
        if (parsedDetails.eventDate) {
          parsedDetails.eventDate = new Date(parsedDetails.eventDate);
        }
        if (parsedDetails.eventEndDate) {
          parsedDetails.eventEndDate = new Date(parsedDetails.eventEndDate);
        }
        setCustomerDetails(parsedDetails);
      } catch (error) {
        console.error('Error parsing stored customer details:', error);
      }
    }
  }, [pathname]); // Re-run when pathname changes (returning from customer details page)

  const handleCustomerDetailsSubmit = useCallback((details: CustomerDetails) => {
    setCustomerDetails(details);
    setIsSheetOpen(false);
  }, []);

  const handleEditCustomerDetails = useCallback(() => {
    // Navigate to customer details page with current details
    const detailsParam = customerDetails ? 
      encodeURIComponent(JSON.stringify(customerDetails)) : '';
    router.push(`${pathname}/customer-details${detailsParam ? `?details=${detailsParam}` : ''}`);
  }, [customerDetails, pathname, router]);

  const handleNavigateToCustomerDetails = useCallback((cartData?: unknown, selectedProducts?: Record<string, number>) => {
    // Store cart data in localStorage before navigating
    if (cartData) {
      localStorage.setItem('cartData', JSON.stringify(cartData));
    }
    // Store selected products state to restore cart when user returns
    if (selectedProducts) {
      localStorage.setItem('selectedProducts', JSON.stringify(selectedProducts));
    }
    // Navigate to customer details page without existing details
    router.push(`${pathname}/customer-details`);
  }, [pathname, router]);

  return {
    customerDetails,
    isSheetOpen,
    setIsSheetOpen,
    handleCustomerDetailsSubmit,
    handleEditCustomerDetails,
    handleNavigateToCustomerDetails
  };
}